import axios from "axios";
import { memoryTokenStore } from "utils/memoryTokenStore";

const axiosServices = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL,
});

axiosServices.interceptors.request.use(
  (config) => {
    // Skip Authorization for logout endpoint
    if (config.url?.endsWith("/logout")) {
      return config;
    }

    const token = memoryTokenStore.getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosServices.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (originalRequest.url?.endsWith("/login")) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axiosServices(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          import.meta.env.VITE_APP_API_URL + "/token/refresh",
          {},
          { withCredentials: true },
        );

        memoryTokenStore.setToken(data.token);

        axiosServices.defaults.headers.Authorization = `Bearer ${data.token}`;
        processQueue(null, data.token);

        return axiosServices(originalRequest);
      } catch (err) {
        processQueue(err, null);
        // logout user
        //localStorage.clear();
        memoryTokenStore.setToken(null);
        window.location.href = "/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default axiosServices;
